import { ClientEndpointType, DetectionZoneDwellEventTrigger, DetectionZonePresenceEventTrigger, Devices, enumKeys, EventTriggerDevice, EventTriggerFacility, EventTriggerType, Facilities, getClientEndpointTypeDisplayName, getTargetTypeDisplayName, ImpendingZoneEntryEventTrigger, MaxServerIntValue, SpeedingEventTrigger, TargetType, WrongWayEventTrigger } from "@app/shared";
import { Model, useFetch } from "@app/shared/src/hooks";
import { CheckboxInput, Input, InputRow, NumericInput, Select } from "@app/dashboard/src/components/shared/inputs";
import { useEffect, useState } from "react";
import { MultiSelect, SimpleOption } from "@app/dashboard/src/components/shared/inputs/MultiSelect";
import { fromMPH, toMPH } from "@app/dashboard/src/components/reports/reportHelpers";
import { EventTriggerClientEndpointsEditor } from "./EventTriggerClientEndpointsEditor";
import { EventTriggerEditorProps, EventTriggerTypes } from "./EventTriggerEditor";
import { isCloudProcessingSupported } from "./cloudProcessing";
import { compareVersions } from "@app/dashboard/src/helpers/versionCompare";

type EditEventTriggerProps = {
    model: Model<EventTriggerTypes>;
    hasScreenline?: boolean;
    hasDetectionZone?: boolean;
    hasLaneLine?: boolean;
} & EventTriggerEditorProps;

const AllTargetTypes = enumKeys(TargetType)
    .sort((a, b) => a.localeCompare(b))
    .filter((k) => k !== "Unknown");

// Speed fields on the device/firmware are stored and evaluated in m/s
// (radar Velocity is m/s — see SpeedingEventDetector). The UI presents
// them in mph; convert at the input boundary in both directions.
const M_S_TO_MPH = 2.237;
const msToMphDisplay = (ms: number | undefined): number | undefined =>
    ms != null ? Math.round(ms * M_S_TO_MPH * 10) / 10 : undefined;
const mphToMsSave = (mph: number | undefined): number | undefined =>
    mph != null ? mph / M_S_TO_MPH : undefined;

export function EditEventTrigger(props: EditEventTriggerProps) {
    const [detectionZones] = useFetch(() => (props.hasDetectionZone ? Facilities.getDetectionZoneOptions(props.trigger?.organizationId, props.facilityId) : Promise.resolve([])), [props.hasDetectionZone, props.trigger?.organizationId, props.facilityId]);
    const [screenLines] = useFetch(() => (props.hasScreenline ? Facilities.getScreenLineOptions(props.trigger?.organizationId, props.facilityId) : Promise.resolve([])), [props.hasScreenline, props.trigger?.organizationId, props.facilityId]);
    const [laneLines] = useFetch(() => (props.hasLaneLine ? Facilities.getLaneLineOptions(props.trigger?.organizationId, props.facilityId) : Promise.resolve([])), [props.hasLaneLine, props.trigger?.organizationId, props.facilityId]);
    const [facilityOptions] = useFetch(() => Facilities.getOptions(props.trigger?.organizationId), [props.trigger?.organizationId]);
    const [deviceOptions] = useFetch(() => Devices.getOptions(props.trigger?.organizationId, props.facilityId), [props.trigger?.organizationId, props.facilityId]);

    // Pin edge-only trigger types (no cloud detector on the server) to on-device processing, including
    // legacy triggers saved before the toggle was gated, so a stale processOnDevice=false can't ship.

    const triggerType = props.trigger?.triggerType;
    const processOnDevice = props.trigger?.processOnDevice;
    // deliversFromDevice is computed server-side from ClientEndpointCapabilities and serialized with the
    // endpoint, so this stays correct as integrations are added without another endpoint-type comparison
    // here. CoreApi pins ProcessOnDevice on save too — this only keeps the form honest before then.
    const hasDeviceOnlyEndpoint = (props.trigger?.eventTriggerClientEndpoints ?? [])
        .some((etc) => etc.clientEndpoint?.deliversFromDevice === true);
    useEffect(() => {
        const mustProcessOnDevice = !isCloudProcessingSupported(triggerType) || hasDeviceOnlyEndpoint;
        if (!props.offlineMode && mustProcessOnDevice && processOnDevice !== true) {
            props.onChange({ ...props.trigger, processOnDevice: true } as EventTriggerTypes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerType, processOnDevice, props.offlineMode, hasDeviceOnlyEndpoint]);

    // minimumFirmwareVersion is computed server-side from FirmwareHelpers and serialized with the
    // endpoint. It constrains only device delivery — CoreApi strips the type from payloads for older
    // firmware — so it is a warning on this trigger's delivery mode, not a gate on the endpoint.
    const gatedEndpoints = (props.trigger?.eventTriggerClientEndpoints ?? [])
        .map((etc) => etc.clientEndpoint)
        .filter((ce) => ce?.minimumFirmwareVersion);
    const needsDeviceFirmwareCheck = !props.offlineMode && processOnDevice === true && gatedEndpoints.length > 0;
    const capturePhoto = props.trigger?.capturePhoto === true;
    // Offline (web config) the only device is the one serving the page, passed in as offlineDevice;
    // the unit's local API has no device-by-facility route to fetch.
    const needsScopeDevices = needsDeviceFirmwareCheck || (capturePhoto && !props.offlineMode);
    const [scopeDevices] = useFetch(() =>
        !needsScopeDevices
            ? Promise.resolve(undefined)
            : props.facilityId
                ? Devices.getByFacilityId(props.facilityId)
                : Devices.getByOrganizationId(props.trigger?.organizationId),
        [needsScopeDevices, props.facilityId, props.trigger?.organizationId]
    );
    // Capture Photo is carried out by the device, and each target gets one photo, taken by whichever capture
    // claims it first. The device's own every-target capture claims it early (first sighting, or 25 m out
    // heading away), so on those devices this trigger gets that earlier photo, not the moment of the event.
    // And a cloud-processed trigger never reaches the device's capture list at all
    // (EventHandlers.UpdateConfiguration skips it), so the box does nothing there.
    const photoWarning = (() => {
        if (!capturePhoto) return undefined;
        // Web config hides the Process On Edge Device box and creates its triggers on-device.
        if (processOnDevice === false && !props.offlineMode)
            return "Capture Photo only works when Process On Edge Device is checked. A cloud-processed trigger can only use a photo the device took for another reason.";
        const candidates = props.offlineMode ? (props.offlineDevice ? [props.offlineDevice] : []) : scopeDevices ?? [];
        const filterIds = (props.trigger?.eventTriggerDevices ?? []).map((d) => d.deviceId);
        const inScope = filterIds.length > 0 ? candidates.filter((d) => filterIds.includes(d.id)) : candidates;
        const everyTarget = inScope.filter((d) => d.takeTargetPhotos);
        if (everyTarget.length === 0) return undefined;
        const names = everyTarget.slice(0, 3).map((d) => d.name || d.id).join(", ");
        return `${everyTarget.length} of ${inScope.length} device(s) have Photograph Every Target on: ${names}${everyTarget.length > 3 ? ", …" : ""}. Those devices photograph each vehicle as soon as they start tracking it, so this trigger's events get that earlier photo instead of one taken at the event. Turn Photograph Every Target off on those devices for event-time photos.`;
    })();
    const deviceDeliveryWarning = (() => {
        if (!scopeDevices?.length) return undefined;
        for (const endpoint of gatedEndpoints) {
            const minimum = endpoint!.minimumFirmwareVersion!;
            const outdated = scopeDevices.filter((d) => compareVersions(d.firmwareVersion ?? "0.0.0.0", minimum) < 0);
            if (outdated.length === 0) continue;
            const names = outdated.slice(0, 3).map((d) => d.name || d.id).join(", ");
            const typeName = getClientEndpointTypeDisplayName(endpoint!.endpointType) ?? "this endpoint type";
            // Only offer the cloud escape hatch where one genuinely exists. Both an edge-only trigger
            // type and a device-delivered endpoint (Drakewell, MS2) pin this trigger on-device and hide
            // the checkbox, so telling either to uncheck it is a dead end.
            const remedy = !isCloudProcessingSupported(triggerType)
                ? `This trigger type is only processed on the edge device, so it cannot deliver to ${typeName} until those devices are upgraded.`
                : endpoint!.deliversFromDevice
                    ? `${typeName} is only delivered from the edge device, so this trigger cannot deliver until those devices are upgraded.`
                    : "Uncheck Process On Edge Device to deliver from the cloud instead.";
            return `${outdated.length} of ${scopeDevices.length} device(s) run firmware below ${minimum} and cannot send ${typeName} from the device: ${names}${outdated.length > 3 ? ", …" : ""}. ${remedy}`;
        }
        return undefined;
    })();

    return (
        <>
            <InputRow>
                {/* No explicit value predates the field and counts as enabled, so the box reads
                    `!== false` and always writes an explicit boolean. */}
                <CheckboxInput
                    label="Enabled"
                    checked={props.trigger?.isEnabled !== false}
                    onChange={(e) => props.onChange({ ...props.trigger, isEnabled: e.target.checked } as EventTriggerTypes)}
                />
                {props.trigger?.isEnabled === false && (
                    <div className="text-gray-500 text-sm">Disabled — this trigger stays configured but never fires until re-enabled.</div>
                )}
            </InputRow>
            {props.hasDetectionZone && (
                <InputRow>
                    <Select
                        label="Detection Zone Filter"
                        emptyOptionText="Any Detection Zone"
                        emptyOptionDisabled={false}
                        optionGroups={detectionZones}
                        value={props.trigger?.detectionZoneId}
                        onChange={(e) => props.onChange({ ...props.trigger, detectionZoneId: e.target.value || undefined } as EventTriggerTypes)}
                    />
                </InputRow>
            )}
            {props.hasScreenline && (
                <InputRow>
                    <Select
                        label="Screen Line Filter"
                        emptyOptionText="Any Screen Line"
                        emptyOptionDisabled={false}
                        optionGroups={screenLines}
                        value={props.trigger?.screenLineId}
                        onChange={(e) => props.onChange({ ...props.trigger, screenLineId: e.target.value || undefined } as EventTriggerTypes)}
                    />
                </InputRow>
            )}
            {props.hasLaneLine && (
                <InputRow>
                    <MultiSelect
                        label="Lane Lines (select at least one)"
                        options={(laneLines ?? []).flatMap((g: any) => g?.options ?? []) as SimpleOption[]}
                        value={props.trigger?.laneLineIds ?? []}
                        onChange={(e) => props.onChange({ ...props.trigger, laneLineIds: e as string[] } as EventTriggerTypes)}
                    />
                    {(!props.trigger?.laneLineIds || props.trigger.laneLineIds.length === 0) && (
                        <div className="text-red-600 text-sm">Select at least one lane line. When a target follows a selected lane line&apos;s path, this event fires.</div>
                    )}
                </InputRow>
            )}
            {!props.offlineMode && (
                <>
                    <InputRow>
                        {facilityOptions && (
                            <MultiSelect
                                label="Location Filters"
                                options={facilityOptions as SimpleOption[]}
                                value={props.trigger?.eventTriggerFacilities?.map((f) => f.facilityId ?? "") ?? []}
                                onChange={(e) => {
                                    props.trigger!.eventTriggerFacilities = (e as string[]).map((e) => ({ facilityId: e, eventTriggerId: props.trigger?.id } as EventTriggerFacility)) as EventTriggerFacility[];
                                    props.onChange(props.trigger!);
                                }}
                            />
                        )}
                    </InputRow>
                    <InputRow>
                        {deviceOptions && (
                            <MultiSelect
                                label="Device Filters"
                                options={deviceOptions as SimpleOption[]}
                                value={props.trigger?.eventTriggerDevices?.map((f) => f.deviceId ?? "") ?? []}
                                allowEmpty={true}
                                onChange={(e) => {
                                    props.trigger!.eventTriggerDevices = (e as string[]).map((e) => ({ deviceId: e, eventTriggerId: props.trigger?.id } as EventTriggerDevice)) as EventTriggerDevice[];
                                    props.onChange(props.trigger!);
                                }}
                            />
                        )}
                    </InputRow>
                </>
            )}
            <InputRow>
                <EventTriggerClientEndpointsEditor
                    organizationId={props.trigger?.organizationId}
                    facilityId={props.facilityId}
                    eventTriggerClientEndpoints={props.trigger?.eventTriggerClientEndpoints}
                    onChange={(endpoints) => {
                        const updatedTrigger = { ...props.trigger!, eventTriggerClientEndpoints: endpoints };
                        props.onChange(updatedTrigger);
                    }}
                />
            </InputRow>
            {props.trigger?.triggerType ===EventTriggerType.DetectionZoneDwell && (
                <InputRow>
                    <NumericInput
                        allowFloat={false}
                        label="Minimum Dwell Time (s)"
                        value={props.trigger?.minValue}
                        onChange={(e) => props.onChange({ ...props.trigger, minValue: e } as EventTriggerTypes)}
                    />
                </InputRow>
            )}
            {props.trigger?.triggerType === EventTriggerType.DetectionZonePresence && (
                <>
                    <InputRow>
                        <NumericInput
                            allowFloat={false}
                            label="Minimum Targets"
                            value={props.trigger?.minValue} onChange={(e) => props.onChange({ ...props.trigger, minValue: e } as EventTriggerTypes)}
                        />
                    </InputRow>
                    <InputRow>
                        <NumericInput
                            allowFloat={false}
                            label="Min Dwell Time (ms)"
                            value={props.trigger?.additionalValue} onChange={(e) => props.onChange({ ...props.trigger, additionalValue: e } as EventTriggerTypes)}
                        />
                    </InputRow>
                </>
            )}
            {props.trigger?.triggerType === EventTriggerType.ImpendingZoneEntry && (
                <>
                    <InputRow>
                        <NumericInput label="Driver Reaction Time" value={props.trigger?.minValue} onChange={(e) => props.onChange({ ...props.trigger, minValue: e } as EventTriggerTypes)} />
                    </InputRow>
                    <InputRow>
                        <NumericInput
                            label="Minimum Vehicle Speed (mph)"
                            value={msToMphDisplay(props.trigger?.additionalValue)}
                            onChange={(e) => props.onChange({ ...props.trigger, additionalValue: mphToMsSave(e ?? undefined) } as EventTriggerTypes)}
                        />
                    </InputRow>
                </>
            )}
            {props.trigger?.triggerType === EventTriggerType.SpeedingEvent && (
                <SpeedingSpeedInputs trigger={props.trigger} onChange={props.onChange} />
            )}
            {props.trigger?.triggerType === EventTriggerType.WrongWayEvent && (
                <InputRow>
                    <NumericInput label="Heading Tolerance (°)" value={props.trigger?.additionalValue} onChange={(e) => props.onChange({ ...props.trigger, additionalValue: e } as EventTriggerTypes)} />
                </InputRow>
            )}
            {props.trigger?.triggerType === EventTriggerType.NearMissEvent && (
                <>
                    <InputRow>
                        <NumericInput label="Max Time to Collision (s)" value={props.trigger?.minValue ?? 3} onChange={(e) => props.onChange({ ...props.trigger, minValue: e } as EventTriggerTypes)} />
                    </InputRow>
                    <InputRow>
                        <NumericInput
                            label="Min Vehicle Speed (mph)"
                            value={msToMphDisplay(props.trigger?.additionalValue ?? 3)}
                            onChange={(e) => props.onChange({ ...props.trigger, additionalValue: mphToMsSave(e ?? undefined) } as EventTriggerTypes)}
                        />
                    </InputRow>
                </>
            )}

            {!props.offlineMode && isCloudProcessingSupported(props.trigger?.triggerType) && !hasDeviceOnlyEndpoint && (
                <InputRow>
                    <CheckboxInput label="Process On Edge Device" checked={props.trigger?.processOnDevice} onChange={(e) => props.onChange({ ...props.trigger, processOnDevice: e.target.checked } as EventTriggerTypes)} />
                    <div className="text-gray-500 text-sm">Uncheck to process this trigger in the cloud instead of on the device.</div>
                </InputRow>
            )}
            {!props.offlineMode && isCloudProcessingSupported(props.trigger?.triggerType) && hasDeviceOnlyEndpoint && (
                <InputRow>
                    <div className="text-gray-500 text-sm">An attached endpoint delivers from the edge device, so this trigger is pinned to on-device processing.</div>
                </InputRow>
            )}
            {!props.offlineMode && !isCloudProcessingSupported(props.trigger?.triggerType) && (
                <InputRow>
                    <div className="text-gray-500 text-sm">This trigger type is processed on the edge device.</div>
                </InputRow>
            )}
            {/* Outside the three delivery-mode branches on purpose: which of them is showing is
                unrelated to whether the attached endpoints can reach these devices at all. */}
            {deviceDeliveryWarning && (
                <InputRow>
                    <div className="text-red-600 text-sm">{deviceDeliveryWarning}</div>
                </InputRow>
            )}
            <InputRow>
                Media Capture:
                <br />
                <CheckboxInput label="Capture Video Clip" checked={props.trigger?.captureVideo ?? false} onChange={(e) => props.onChange({ ...props.trigger, captureVideo: e.target.checked } as EventTriggerTypes)} />
                {props.trigger?.captureVideo && (
                    <NumericInput
                        allowFloat={false}
                        min={1}
                        max={365}
                        label="Clip Retention (days), 1–365 — leave blank for default (30)"
                        value={props.trigger?.mediaRetentionDays ?? undefined}
                        onChange={(e) => props.onChange({ ...props.trigger, mediaRetentionDays: e } as EventTriggerTypes)}
                    />
                )}
                <CheckboxInput label="Capture Photo" checked={props.trigger?.capturePhoto ?? false} onChange={(e) => props.onChange({ ...props.trigger, capturePhoto: e.target.checked } as EventTriggerTypes)} />
                {props.trigger?.capturePhoto && (
                    <div className="text-gray-500 text-sm">
                        Photographs the event&apos;s target even when the device&apos;s &quot;Photograph Every Target&quot; setting is off, and
                        regardless of its minimum time between photos. Screen line, zone and lane line triggers take it as the vehicle
                        crosses, enters or rides; other types take it when the event fires. Each vehicle gets one photo: if another
                        Capture Photo trigger&apos;s line, zone or lane reaches it first, this event uses that photo.
                    </div>
                )}
                {photoWarning && <div className="text-red-600 text-sm">{photoWarning}</div>}
            </InputRow>
            <InputRow>
                Target Filters:
                <br />
                <div className="flex flex-row place-items-center gap-2">
                    <label>
                        <input
                            className="border-0 shadow-none w-8"
                            type="checkbox"
                            onChange={(e) => {
                                if (props.trigger == null) return;
                                props.trigger.targetFilter = e.target.checked ? MaxServerIntValue : 0;
                                props.onChange(props.trigger);
                            }}
                            checked={props.trigger?.targetFilter === MaxServerIntValue}
                        />
                        All Targets
                    </label>
                </div>
                {AllTargetTypes.map((t) => (
                    <div key={t} className="flex flex-row place-items-center gap-2">
                        <label>
                            <input
                                className="border-0 shadow-none w-8"
                                type="checkbox"
                                onChange={(e) => {
                                    if (props.trigger?.targetFilter == null) return;
                                    if (e.target.checked) {
                                        props.trigger.targetFilter |= TargetType[t];
                                        props.onChange(props.trigger);
                                    } else {
                                        props.trigger.targetFilter ^= TargetType[t];
                                        props.onChange(props.trigger);
                                    }
                                }}
                                checked={((props.trigger?.targetFilter ?? 0) & TargetType[t]) > 0}
                            />
                            {getTargetTypeDisplayName(t)}
                        </label>
                    </div>
                ))}
            </InputRow>
        </>
    );
}

// Local-state inputs for Speeding's min/max. Two reasons this exists instead of plain NumericInput:
//   1. NumericInput's debounced onChange fires with `undefined` while the user is mid-edit (empty
//      string between keystrokes), which was wiping minValue/maxValue out of the model. Holding a
//      string draft locally and only committing on a valid parse avoids that.
//   2. SpeedingEventTrigger on the server exposes `MinSpeed`/`MaxSpeed` convenience setters that
//      forward to `MinValue`/`MaxValue`. If we sent both the alias keys and the canonical keys in
//      the same payload, deserialization order would let the alias setters overwrite the values we
//      just wrote — so we destructure `minSpeed`/`maxSpeed` off before forwarding to onChange.
type SpeedingSpeedInputsProps = {
    trigger: EventTriggerTypes;
    onChange: (trigger: EventTriggerTypes | undefined) => any;
};

// Display helper for the free-text speed inputs below: returns a string ("" when empty)
// for the controlled <Input>. Distinct from the numeric `msToMphDisplay` above, which
// feeds <NumericInput> for the other trigger types.
const msToMphText = (ms: number | null | undefined) =>
    ms != null ? String(Math.round(toMPH(ms) * 10) / 10) : "";

const SpeedingSpeedInputs: React.FC<SpeedingSpeedInputsProps> = ({ trigger, onChange }) => {
    const [minDraft, setMinDraft] = useState(() => msToMphText(trigger.minValue));
    const [maxDraft, setMaxDraft] = useState(() => msToMphText(trigger.maxValue));

    // Resync from props when the underlying value changes (e.g. after save reload).
    useEffect(() => { setMinDraft(msToMphText(trigger.minValue)); }, [trigger.minValue]);
    useEffect(() => { setMaxDraft(msToMphText(trigger.maxValue)); }, [trigger.maxValue]);

    const commit = (field: "minValue" | "maxValue", draft: string) => {
        const trimmed = draft.trim();
        const parsed = trimmed === "" ? undefined : parseFloat(trimmed.replace(",", "."));
        const ms = parsed != null && !isNaN(parsed) ? fromMPH(parsed) : undefined;
        const { minSpeed, maxSpeed, ...rest } = trigger as any;
        onChange({ ...rest, [field]: ms } as EventTriggerTypes);
    };

    // Commit on every keystroke that parses to a valid number, so the latest value is in the
    // model by the time the user clicks Save (no need to tab out first). Empty input doesn't
    // auto-commit — onBlur handles the "clear" case explicitly.
    const handleChange = (field: "minValue" | "maxValue", raw: string) => {
        if (field === "minValue") setMinDraft(raw); else setMaxDraft(raw);
        const trimmed = raw.trim();
        if (trimmed === "") return;
        const parsed = parseFloat(trimmed.replace(",", "."));
        if (!isNaN(parsed)) {
            const ms = fromMPH(parsed);
            const { minSpeed, maxSpeed, ...rest } = trigger as any;
            onChange({ ...rest, [field]: ms } as EventTriggerTypes);
        }
    };

    return (
        <InputRow>
            <Input
                label="Minimum Speed (mph)"
                type="number"
                step="0.1"
                value={minDraft}
                onChange={(e) => handleChange("minValue", e.target.value)}
                onBlur={() => commit("minValue", minDraft)}
            />
            <Input
                label="Maximum Speed (mph)"
                type="number"
                step="0.1"
                value={maxDraft}
                onChange={(e) => handleChange("maxValue", e.target.value)}
                onBlur={() => commit("maxValue", maxDraft)}
            />
        </InputRow>
    );
};
